import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Button,
  Grid,
  Collapse,
  IconButton,
  Chip,
  TableContainer,
  TablePagination
} from "@mui/material";
import { styled } from "@mui/material/styles";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import { fetchToken } from "../auth/Token";
import { api } from "../api";
import { toast } from "sonner";
import { TextField, Autocomplete } from "@mui/material";
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import { useNavigate } from 'react-router-dom';


// Styled components
const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(2),
  borderStyle: "groove",
  border: "3px",
  borderColor: "grey",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2), 0 6px 20px rgba(0, 0, 0, 0.2)",
  marginTop: "10px",
  minHeight: "50px",
  borderRadius: "10px",
}));

const IncomeStatementTable = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    charges: false,
    directIncome: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!data || !data.periods) return null;

  // Determine columns based on period type
  const getColumns = () => {
    switch (data.periodic_state) {
      case 'yearly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'quarterly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'monthly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'weekly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      default:
        return [];
    }
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate row totals
  const calculateRowTotal = (category, key) => {
    if (!data.periods || data.periods.length === 0) return 0;
    
    if (category === 'total_income') {
      return data.total_income;
    }
    if (category === 'total_charges') {
      return data.total_charges;
    }
    if (category === 'total_direct_income') {
      return data.total_direct_income;
    }
    if (category === 'charges') {
      return data.periods.reduce((sum, period) => sum + (period.charges[key] || 0), 0);
    }
    if (category === 'direct_income') {
      return data.periods.reduce((sum, period) => sum + (period.direct_income[key] || 0), 0);
    }
    return 0;
  };

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 340,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="income statement table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b></b>
              </Typography>
            </TableCell>

            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Total Charges */}
          <TableRow sx={{ backgroundColor: "#f5f3f4" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('charges')}>
                  {expandedCategories.charges ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Total Charges</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex];
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.charges.total_charges)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('total_charges'))}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Charges Categories - Collapsible */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.charges} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {Object.entries(data.periods[0]?.charges || {}).map(([key, value]) => {
                      if (key === 'total_charges') return null;
                      return (
                        <TableRow key={key}>
                          <TableCell align="left" sx={{ pl: 6 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                            </Typography>
                          </TableCell>
                          {columns.map((col, colIndex) => {
                            const period = data.periods[colIndex];
                            return (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(period.charges[key] || 0)}</b>
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(calculateRowTotal('charges', key))}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Total Direct Income */}
          <TableRow sx={{ backgroundColor: "#f5f3f4" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('directIncome')}>
                  {expandedCategories.directIncome ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Total Direct Income</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex];
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.direct_income.total_direct_income)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('total_direct_income'))}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Direct Income Categories - Collapsible */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.directIncome} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {Object.entries(data.periods[0]?.direct_income || {}).map(([key, value]) => {
                      if (key === 'total_direct_income') return null;
                      return (
                        <TableRow key={key}>
                          <TableCell align="left" sx={{ pl: 6 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                            </Typography>
                          </TableCell>
                          {columns.map((col, colIndex) => {
                            const period = data.periods[colIndex];
                            return (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(period.direct_income[key] || 0)}</b>
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(calculateRowTotal('direct_income', key))}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Total Income */}
          <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total Income</b>
              </Typography>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex];
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.total_income)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('total_income'))}</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};


const InflowsTable = ({ data }) => {
  if (!data || !data.periods) return null;

  // Determine columns based on period type
  const getColumns = () => {
    switch (data.periodic_state) {
      case 'yearly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'quarterly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'monthly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'weekly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      default:
        return [];
    }
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Calculate row totals
  const calculateRowTotal = (key) => {
    if (!data.periods || data.periods.length === 0) return 0;
    return data.periods.reduce((sum, period) => sum + (period.income[key] || 0), 0);
  };

  // Get all income keys except 'total_income'
  const incomeKeys = Object.keys(data.periods[0]?.income || {}).filter(key => key !== 'total_income');

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 340,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="inflows table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Inflow Items</b>
              </Typography>
            </TableCell>

            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* List all inflow items directly */}
          {incomeKeys.map((key) => (
            <TableRow key={key}>
              <TableCell align="left">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                </Typography>
              </TableCell>
              {columns.map((col, colIndex) => {
                const period = data.periods[colIndex];
                return (
                  <TableCell key={colIndex} align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(period.income[key] || 0)}</b>
                    </Typography>
                  </TableCell>
                );
              })}
              <TableCell align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{formatCurrency(calculateRowTotal(key))}</b>
                </Typography>
              </TableCell>
            </TableRow>
          ))}

          {/* Total Inflows row at the bottom */}
          <TableRow sx={{ backgroundColor: "#f5f3f4" }}>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total Inflows</b>
              </Typography>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex];
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.income.total_income)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.total_income)}</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const ExpensesTable = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    operational: false,
    staff: false,
    assets: false,
    logistics: false,
    financials: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!data || !data.periods || data.periods.length === 0) {
    return <Typography>Loading data or no data available...</Typography>;
  }

  // Determine columns based on period type
  const getColumns = () => {
    const periodMap = {
      yearly: 'Year',
      quarterly: 'Quarter',
      monthly: 'Month',
      weekly: 'Week'
    };

    return data.periods.map(period => ({
      name: period.name,
      start: period.start_date,
      end: period.end_date,
      type: periodMap[data.periodic_state] || 'Period'
    }));
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate row totals
  const calculateRowTotal = (category, key) => {
    if (!data.periods || data.periods.length === 0) return 0;
    return data.periods.reduce((sum, period) => sum + (period[category]?.[key] || 0), 0);
  };

  // Get first period for category keys
  const firstPeriod = data.periods[0] || {};

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 500,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="expenses table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Expense Categories</b>
              </Typography>
            </TableCell>
            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
                <Typography variant="caption" display="block">
                  {formatDate(col.start)} - {formatDate(col.end)}
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Operational Expenses */}
          <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('operational')}>
                  {expandedCategories.operational ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Operational Expenses</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.operational_expenses?.total_operational)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.total_operational)}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Operational Expenses Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.operational} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {firstPeriod.operational_expenses && Object.entries(firstPeriod.operational_expenses)
                      .filter(([key]) => key !== 'total_operational')
                      .map(([key]) => (
                        <TableRow key={key}>
                          <TableCell align="left" sx={{ pl: 6 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                            </Typography>
                          </TableCell>
                          {columns.map((col, colIndex) => {
                            const period = data.periods[colIndex] || {};
                            return (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(period.operational_expenses?.[key])}</b>
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(calculateRowTotal('operational_expenses', key))}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Staff Expenses */}
          <TableRow sx={{ backgroundColor: "#fff0f3" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('staff')}>
                  {expandedCategories.staff ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Staff Expenses</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.staff_expenses?.total_staff)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.total_staff)}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Staff Expenses Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.staff} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {firstPeriod.staff_expenses && Object.entries(firstPeriod.staff_expenses)
                      .filter(([key]) => key !== 'total_staff')
                      .map(([key]) => (
                        <TableRow key={key}>
                          <TableCell align="left" sx={{ pl: 6 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                            </Typography>
                          </TableCell>
                          {columns.map((col, colIndex) => {
                            const period = data.periods[colIndex] || {};
                            return (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(period.staff_expenses?.[key])}</b>
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(calculateRowTotal('staff_expenses', key))}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
          
         {/* Logistics Expenses */}
            <TableRow sx={{ backgroundColor: "#ffedd8" }}>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  <IconButton size="small" onClick={() => toggleCategory('logistics')}>
                    {expandedCategories.logistics ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                  </IconButton>
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Logistic Expenses</b>
                  </Typography>
                </Box>
              </TableCell>
              {columns.map((col, colIndex) => {
                const period = data.periods[colIndex] || {};
                return (
                  <TableCell key={colIndex} align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(period.logistic_expenses?.total_assigned_value)}</b>
                    </Typography>
                  </TableCell>
                );
              })}
              <TableCell align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{formatCurrency(data.total_logistic)}</b>
                </Typography>
              </TableCell>
            </TableRow>

            {/* Logistic Expenses Details */}
            <TableRow>
              <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                <Collapse in={expandedCategories.logistics} timeout="auto" unmountOnExit>
                  <Table size="small">
                    <TableBody>
                      {/* Summary of assigned items */}
                      <TableRow>
                        <TableCell align="left" sx={{ pl: 6 }}>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Total Assigned Items Value</b>
                          </Typography>
                        </TableCell>
                        {columns.map((col, colIndex) => {
                          const period = data.periods[colIndex] || {};
                          return (
                            <TableCell key={colIndex} align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(period.logistic_expenses?.total_assigned_value)}</b>
                              </Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>{formatCurrency(data.total_logistic)}</b>
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* All logistic items */}
                      {(() => {
                        // Collect all items across all periods
                        const allItems = {};
                        data.periods.forEach(period => {
                          const items = period.logistic_expenses?.all_items || [];
                          items.forEach(item => {
                            if (!allItems[item.item_id]) {
                              allItems[item.item_id] = {
                                id: item.item_id,
                                name: item.item_name,
                                amounts: Array(columns.length).fill(0),
                                assignments: Array(columns.length).fill([]),
                                has_assignments: Array(columns.length).fill(false)
                              };
                            }
                          });
                        });

                        // Populate data for each item
                        data.periods.forEach((period, periodIndex) => {
                          const items = period.logistic_expenses?.all_items || [];
                          items.forEach(item => {
                            if (allItems[item.item_id] && periodIndex < columns.length) {
                              allItems[item.item_id].amounts[periodIndex] = item.total_value;
                              allItems[item.item_id].assignments[periodIndex] = item.assignments;
                              allItems[item.item_id].has_assignments[periodIndex] = item.has_assignments;
                            }
                          });
                        });

                        return Object.values(allItems).map((item, index) => (
                          <React.Fragment key={index}>
                            <TableRow sx={{ 
                              backgroundColor: item.amounts.some(amt => amt > 0) ? '#fff8e1' : 'inherit',
                              '&:hover': { backgroundColor: '#fff3e0' }
                            }}>
                              <TableCell align="left" sx={{ pl: 6 }}>
                                <Box display="flex" alignItems="center">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{item.name}</b>
                                  </Typography>
                                  {!item.amounts.some(amt => amt > 0) && (
                                    <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                      (No assignments)
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              {item.amounts.map((amount, colIndex) => (
                                <TableCell key={colIndex} align="right">
                                  <Typography 
                                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                                    sx={{ 
                                      fontWeight: item.has_assignments[colIndex] ? 'bold' : 'normal',
                                      color: item.has_assignments[colIndex] ? 'inherit' : 'text.secondary'
                                    }}
                                  >
                                    {formatCurrency(amount)}
                                  </Typography>
                                </TableCell>
                              ))}
                              <TableCell align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(item.amounts.reduce((sum, a) => sum + a, 0))}</b>
                                </Typography>
                              </TableCell>
                            </TableRow>

                            {/* Assignment details for each item */}
                            {columns.map((col, colIndex) => {
                              const assignments = item.assignments[colIndex] || [];
                              if (assignments.length === 0) return null;

                              return (
                                <TableRow key={`assignments-${colIndex}`}>
                                  <TableCell colSpan={columns.length + 2} sx={{ pl: 9, py: 0 }}>
                                    <Box sx={{ ml: 2, borderLeft: '2px solid #ddd', pl: 2 }}>
                                      <Typography variant="caption" display="block" sx={{ fontWeight: 'bold' }}>
                                        Assignments in {col.name}:
                                      </Typography>
                                      {assignments.map((assignment, aIndex) => (
                                        <Box key={aIndex} sx={{ mb: 1 }}>
                                          <Typography variant="caption">
                                            {assignment.quantity} units to {assignment.level} on {formatDate(assignment.date)}
                                          </Typography>
                                          <Typography variant="caption" display="block">
                                            Value: {formatCurrency(assignment.value)}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </React.Fragment>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </Collapse>
              </TableCell>
          </TableRow>
          
          {/* Financial Cost */}
            <TableRow sx={{ backgroundColor: "#e9ecef" }}>
              <TableCell align="left">
                <Box display="flex" alignItems="center">
                  <IconButton size="small" onClick={() => toggleCategory('financials')}>
                    {expandedCategories.financials ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                  </IconButton>
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Financial Cost</b>
                  </Typography>
                </Box>
              </TableCell>
              {columns.map((col, colIndex) => {
                const period = data.periods[colIndex] || {};
                return (
                  <TableCell key={colIndex} align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(period.financial_cost?.total_financial_cost)}</b>
                    </Typography>
                  </TableCell>
                );
              })}
              <TableCell align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{formatCurrency(data.total_financial_cost)}</b>
                </Typography>
              </TableCell>
            </TableRow>

            {/* Financial Cost Details */}
            <TableRow>
              <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                <Collapse in={expandedCategories.financials} timeout="auto" unmountOnExit>
                  <Table size="small">
                    <TableBody>
                      {/* Interest on Investments */}
                      <TableRow>
                        <TableCell align="left" sx={{ pl: 6 }}>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Interest on Investments</b>
                          </Typography>
                        </TableCell>
                        {columns.map((col, colIndex) => {
                          const period = data.periods[colIndex] || {};
                          return (
                            <TableCell key={colIndex} align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(period.financial_cost?.interest_on_investments)}</b>
                              </Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>{formatCurrency(calculateRowTotal('financial_cost', 'interest_on_investments'))}</b>
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Interest on Shares */}
                      <TableRow>
                        <TableCell align="left" sx={{ pl: 6 }}>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Interest on Shares</b>
                          </Typography>
                        </TableCell>
                        {columns.map((col, colIndex) => {
                          const period = data.periods[colIndex] || {};
                          return (
                            <TableCell key={colIndex} align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(period.financial_cost?.interest_on_shares)}</b>
                              </Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>{formatCurrency(calculateRowTotal('financial_cost', 'interest_on_shares'))}</b>
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Account Charges */}
                      <TableRow>
                        <TableCell align="left" sx={{ pl: 6 }}>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Account Charges</b>
                          </Typography>
                        </TableCell>
                        {columns.map((col, colIndex) => {
                          const period = data.periods[colIndex] || {};
                          return (
                            <TableCell key={colIndex} align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(period.financial_cost?.total_account_charges)}</b>
                              </Typography>
                            </TableCell>
                          );
                        })}
                        <TableCell align="right">
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>{formatCurrency(calculateRowTotal('financial_cost', 'total_account_charges'))}</b>
                          </Typography>
                        </TableCell>
                      </TableRow>

                      {/* Account Charge Details */}
                      {(() => {
                        // Collect all unique account charges across all periods
                        const allAccountCharges = {};
                        data.periods.forEach((period, periodIndex) => {
                          const details = period.financial_cost?.details || [];
                          details.forEach(charge => {
                            const key = `${charge.account}-${charge.date_month}`;
                            if (!allAccountCharges[key]) {
                              allAccountCharges[key] = {
                                account: charge.account,
                                date: charge.date_month,
                                amounts: Array(columns.length).fill(0),
                                note: charge.note
                              };
                            }
                            if (periodIndex < columns.length) {
                              allAccountCharges[key].amounts[periodIndex] = charge.total_charges;
                            }
                          });
                        });

                        return Object.values(allAccountCharges).map((charge, index) => (
                          <TableRow key={index}>
                            <TableCell align="left" sx={{ pl: 9 }}>
                              <Box>
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{charge.account}</b>
                                </Typography>
                                <Typography variant="caption" display="block">
                                  {formatDate(charge.date)} - {charge.note || 'No note'}
                                </Typography>
                              </Box>
                            </TableCell>
                            {charge.amounts.map((amount, colIndex) => (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(amount)}</b>
                                </Typography>
                              </TableCell>
                            ))}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(charge.amounts.reduce((sum, a) => sum + a, 0))}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ));
                      })()}
                    </TableBody>
                  </Table>
                </Collapse>
              </TableCell>
            </TableRow>

          {/* Asset Expenses */}
          <TableRow sx={{ backgroundColor: "#f0fff4" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('assets')}>
                  {expandedCategories.assets ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Asset Expenses</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.asset_expenses?.total_assets)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.total_assets)}</b>
              </Typography>
            </TableCell>
          </TableRow>

         {/* Asset Expenses Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.assets} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Regular Asset Expenses */}
                    {firstPeriod.asset_expenses && Object.entries(firstPeriod.asset_expenses)
                      .filter(([key]) => !['total_assets', 'depreciation_details', 'depreciation'].includes(key))
                      .map(([key]) => (
                        <TableRow key={key}>
                          <TableCell align="left" sx={{ pl: 6 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                            </Typography>
                          </TableCell>
                          {columns.map((col, colIndex) => {
                            const period = data.periods[colIndex] || {};
                            return (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(period.asset_expenses?.[key])}</b>
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(calculateRowTotal('asset_expenses', key))}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}

                    {/* Depreciation Line */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Depreciation</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.asset_expenses?.depreciation)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(calculateRowTotal('asset_expenses', 'depreciation'))}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Simplified Depreciation Details - Just asset names and amounts */}
                    {(() => {
                      // Collect all unique assets across all periods
                      const allAssets = {};
                      data.periods.forEach(period => {
                        const details = period.asset_expenses?.depreciation_details || [];
                        details.forEach(asset => {
                          if (!allAssets[asset.asset_name]) {
                            allAssets[asset.asset_name] = {
                              name: asset.asset_name,
                              amounts: Array(columns.length).fill(0)
                            };
                          }
                        });
                      });

                      // Populate amounts for each asset
                      data.periods.forEach((period, periodIndex) => {
                        const details = period.asset_expenses?.depreciation_details || [];
                        details.forEach(asset => {
                          if (allAssets[asset.asset_name] && periodIndex < columns.length) {
                            allAssets[asset.asset_name].amounts[periodIndex] = asset.depreciated_amount;
                          }
                        });
                      });

                      return Object.values(allAssets).map((asset, index) => (
                        <TableRow key={index}>
                          <TableCell align="left" sx={{ pl: 9 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{asset.name}</b>
                            </Typography>
                          </TableCell>
                          {asset.amounts.map((amount, colIndex) => (
                            <TableCell key={colIndex} align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(amount)}</b>
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(asset.amounts.reduce((sum, a) => sum + a, 0))}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ));
                    })()}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          

          {/* Total Expenses */}
          <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total Expenses</b>
              </Typography>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.total_expenses)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.total_expenses)}</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const IncomeExpenseTable = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    income: false,
    charges: false,
    directIncome: false,
    expenses: false,
    operational: false,
    staff: false,
    assets: false,
    logistics: false,
    financials: false
  });
  
  // console.log(data)

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!data || !data.periods || data.periods.length === 0) {
    return <Typography>Loading data or no data available...</Typography>;
  }

  // Determine columns based on period type
  const getColumns = () => {
    const periodMap = {
      yearly: 'Year',
      quarterly: 'Quarter',
      monthly: 'Month',
      weekly: 'Week'
    };

    return data.periods.map(period => ({
      name: period.name,
      start: period.start_date,
      end: period.end_date,
      type: periodMap[data.periodic_state] || 'Period'
    }));
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate row totals
  const calculateRowTotal = (category, key) => {
    if (!data.periods || data.periods.length === 0) return 0;
    return data.periods.reduce((sum, period) => sum + (period[category]?.[key] || 0), 0);
  };

  // Get first period for category keys
  const firstPeriod = data.periods[0] || {};

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 500,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="income and expense table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Categories</b>
              </Typography>
            </TableCell>
            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
                <Typography variant="caption" display="block">
                  {formatDate(col.start)} - {formatDate(col.end)}
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Income Section */}
          <TableRow sx={{ backgroundColor: "#e6f7ff" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('income')}>
                  {expandedCategories.income ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Total Income</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.income?.total)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.summary?.total_income)}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Income Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.income} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Charges Income */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('charges')}>
                            {expandedCategories.charges ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Charges & Fees</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.income?.charges?.total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.income_categories?.charges)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Charges Details */}
                    <TableRow>
                      <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                        <Collapse in={expandedCategories.charges} timeout="auto" unmountOnExit>
                          <Table size="small">
                            <TableBody>
                              {firstPeriod.income?.charges && Object.entries(firstPeriod.income.charges)
                                .filter(([key]) => key !== 'total')
                                .map(([key]) => (
                                  <TableRow key={key}>
                                    <TableCell align="left" sx={{ pl: 12 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                                      </Typography>
                                    </TableCell>
                                    {columns.map((col, colIndex) => {
                                      const period = data.periods[colIndex] || {};
                                      return (
                                        <TableCell key={colIndex} align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(period.income?.charges?.[key])}</b>
                                          </Typography>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(calculateRowTotal('income.charges', key))}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                    {/* Direct Income */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('directIncome')}>
                            {expandedCategories.directIncome ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Direct Income</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.income?.direct_income?.total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.income_categories?.direct_income)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Direct Income Details */}
                    <TableRow>
                      <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                        <Collapse in={expandedCategories.directIncome} timeout="auto" unmountOnExit>
                          <Table size="small">
                            <TableBody>
                              {firstPeriod.income?.direct_income && Object.entries(firstPeriod.income.direct_income)
                                .filter(([key]) => key !== 'total')
                                .map(([key]) => (
                                  <TableRow key={key}>
                                    <TableCell align="left" sx={{ pl: 12 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                                      </Typography>
                                    </TableCell>
                                    {columns.map((col, colIndex) => {
                                      const period = data.periods[colIndex] || {};
                                      return (
                                        <TableCell key={colIndex} align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(period.income?.direct_income?.[key])}</b>
                                          </Typography>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(calculateRowTotal('income.direct_income', key))}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Expenses Section */}
          <TableRow sx={{ backgroundColor: "#fff1f0" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('expenses')}>
                  {expandedCategories.expenses ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Total Expenses</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.expenses?.total)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.summary?.total_expenses)}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Expenses Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.expenses} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Operational Expenses */}
                    <TableRow sx={{ backgroundColor: "#fff2e8" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('operational')}>
                            {expandedCategories.operational ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Operational Expenses</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.expenses?.operational?.total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.expense_categories?.operational)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Operational Expenses Details */}
                    <TableRow>
                      <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                        <Collapse in={expandedCategories.operational} timeout="auto" unmountOnExit>
                          <Table size="small">
                            <TableBody>
                              {firstPeriod.expenses?.operational && Object.entries(firstPeriod.expenses.operational)
                                .filter(([key]) => key !== 'total')
                                .map(([key]) => (
                                  <TableRow key={key}>
                                    <TableCell align="left" sx={{ pl: 12 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                                      </Typography>
                                    </TableCell>
                                    {columns.map((col, colIndex) => {
                                      const period = data.periods[colIndex] || {};
                                      return (
                                        <TableCell key={colIndex} align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(period.expenses?.operational?.[key])}</b>
                                          </Typography>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(calculateRowTotal('expenses.operational', key))}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                    {/* Staff Expenses */}
                    <TableRow sx={{ backgroundColor: "#fff2e8" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('staff')}>
                            {expandedCategories.staff ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Staff Expenses</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.expenses?.staff?.total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.expense_categories?.staff)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Staff Expenses Details */}
                    <TableRow>
                      <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                        <Collapse in={expandedCategories.staff} timeout="auto" unmountOnExit>
                          <Table size="small">
                            <TableBody>
                              {firstPeriod.expenses?.staff && Object.entries(firstPeriod.expenses.staff)
                                .filter(([key]) => key !== 'total')
                                .map(([key]) => (
                                  <TableRow key={key}>
                                    <TableCell align="left" sx={{ pl: 12 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                                      </Typography>
                                    </TableCell>
                                    {columns.map((col, colIndex) => {
                                      const period = data.periods[colIndex] || {};
                                      return (
                                        <TableCell key={colIndex} align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(period.expenses?.staff?.[key])}</b>
                                          </Typography>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(calculateRowTotal('expenses.staff', key))}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                    {/* Logistic Expenses */}
                    <TableRow sx={{ backgroundColor: "#fff2e8" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('logistics')}>
                            {expandedCategories.logistics ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Logistic Expenses</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.expenses?.logistic_expenses?.total_logistic)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.expense_categories?.logistic)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Logistic Expenses Details */}
                    <TableRow>
                      <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                        <Collapse in={expandedCategories.logistics} timeout="auto" unmountOnExit>
                          <Table size="small">
                            <TableBody>
                              {firstPeriod.expenses?.logistic_expenses && Object.entries(firstPeriod.expenses.logistic_expenses)
                                .filter(([key]) => key !== 'total')
                                .map(([key]) => (
                                  <TableRow key={key}>
                                    <TableCell align="left" sx={{ pl: 12 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                                      </Typography>
                                    </TableCell>
                                    {columns.map((col, colIndex) => {
                                      const period = data.periods[colIndex] || {};
                                      return (
                                        <TableCell key={colIndex} align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(period.expenses?.logistic_expenses?.[key])}</b>
                                          </Typography>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(calculateRowTotal('expenses.logistic_expenses', key))}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                    {/* Financial Cost */}
                        <TableRow sx={{ backgroundColor: "#fff2e8" }}>
                          <TableCell align="left">
                            <Box display="flex" alignItems="center">
                              <IconButton size="small" onClick={() => toggleCategory('financials')}>
                                {expandedCategories.financials ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                              </IconButton>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Financial Cost</b>
                              </Typography>
                            </Box>
                          </TableCell>
                          {columns.map((col, colIndex) => {
                            const period = data.periods[colIndex] || {};
                            return (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(period.expenses.financial_cost?.total_financial_cost)}</b>
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(data.total_financial_cost)}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>

                            {/* Financial Cost Details */}
                            <TableRow>
                              <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                                <Collapse in={expandedCategories.financials} timeout="auto" unmountOnExit>
                                  <Table size="small">
                                    <TableBody>
                                      {/* Interest on Investments */}
                                      <TableRow>
                                        <TableCell align="left" sx={{ pl: 6 }}>
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>Interest on Investments</b>
                                          </Typography>
                                        </TableCell>
                                        {columns.map((col, colIndex) => {
                                          const period = data.periods[colIndex] || {};
                                          return (
                                            <TableCell key={colIndex} align="right">
                                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                <b>{formatCurrency(period.expenses.financial_cost?.interest_on_investments)}</b>
                                              </Typography>
                                            </TableCell>
                                          );
                                        })}
                                        <TableCell align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(calculateRowTotal('financial_cost', 'interest_on_investments'))}</b>
                                          </Typography>
                                        </TableCell>
                                      </TableRow>

                                      {/* Interest on Shares */}
                                      <TableRow>
                                        <TableCell align="left" sx={{ pl: 6 }}>
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>Interest on Shares</b>
                                          </Typography>
                                        </TableCell>
                                        {columns.map((col, colIndex) => {
                                          const period = data.periods[colIndex] || {};
                                          return (
                                            <TableCell key={colIndex} align="right">
                                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                <b>{formatCurrency(period.expenses.financial_cost?.interest_on_shares)}</b>
                                              </Typography>
                                            </TableCell>
                                          );
                                        })}
                                        <TableCell align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(calculateRowTotal('financial_cost', 'interest_on_shares'))}</b>
                                          </Typography>
                                        </TableCell>
                                      </TableRow>

                                      {/* Account Charges */}
                                      <TableRow>
                                        <TableCell align="left" sx={{ pl: 6 }}>
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>Account Charges</b>
                                          </Typography>
                                        </TableCell>
                                        {columns.map((col, colIndex) => {
                                          const period = data.periods[colIndex] || {};
                                          return (
                                            <TableCell key={colIndex} align="right">
                                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                <b>{formatCurrency(period.expenses.financial_cost?.total_account_charges)}</b>
                                              </Typography>
                                            </TableCell>
                                          );
                                        })}
                                        <TableCell align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(calculateRowTotal('financial_cost', 'total_account_charges'))}</b>
                                          </Typography>
                                        </TableCell>
                                      </TableRow>

                                      {/* Account Charge Details */}
                                      {(() => {
                                        // Collect all unique account charges across all periods
                                        const allAccountCharges = {};
                                        data.periods.forEach((period, periodIndex) => {
                                          const details = period.expenses.financial_cost?.details || [];
                                          details.forEach(charge => {
                                            const key = `${charge.account}-${charge.date_month}`;
                                            if (!allAccountCharges[key]) {
                                              allAccountCharges[key] = {
                                                account: charge.account,
                                                date: charge.date_month,
                                                amounts: Array(columns.length).fill(0),
                                                note: charge.note
                                              };
                                            }
                                            if (periodIndex < columns.length) {
                                              allAccountCharges[key].amounts[periodIndex] = charge.total_charges;
                                            }
                                          });
                                        });

                                        return Object.values(allAccountCharges).map((charge, index) => (
                                          <TableRow key={index}>
                                            <TableCell align="left" sx={{ pl: 9 }}>
                                              <Box>
                                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                  <b>{charge.account}</b>
                                                </Typography>
                                                <Typography variant="caption" display="block">
                                                  {formatDate(charge.date)} - {charge.note || 'No note'}
                                                </Typography>
                                              </Box>
                                            </TableCell>
                                            {charge.amounts.map((amount, colIndex) => (
                                              <TableCell key={colIndex} align="right">
                                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                  <b>{formatCurrency(amount)}</b>
                                                </Typography>
                                              </TableCell>
                                            ))}
                                            <TableCell align="right">
                                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                                <b>{formatCurrency(charge.amounts.reduce((sum, a) => sum + a, 0))}</b>
                                              </Typography>
                                            </TableCell>
                                          </TableRow>
                                        ));
                                      })()}
                                    </TableBody>
                                  </Table>
                                </Collapse>
                              </TableCell>
                            </TableRow>

                    {/* Asset Expenses */}
                    <TableRow sx={{ backgroundColor: "#fff2e8" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('assets')}>
                            {expandedCategories.assets ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Asset Expenses</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.expenses?.assets?.total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.expense_categories?.assets)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Asset Expenses Details */}
                    <TableRow>
                      <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                        <Collapse in={expandedCategories.assets} timeout="auto" unmountOnExit>
                          <Table size="small">
                            <TableBody>
                              {firstPeriod.expenses?.assets && Object.entries(firstPeriod.expenses.assets)
                                .filter(([key]) => key !== 'total' && key !== 'depreciation_details')
                                .map(([key]) => (
                                  <TableRow key={key}>
                                    <TableCell align="left" sx={{ pl: 12 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</b>
                                      </Typography>
                                    </TableCell>
                                    {columns.map((col, colIndex) => {
                                      const period = data.periods[colIndex] || {};
                                      return (
                                        <TableCell key={colIndex} align="right">
                                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                            <b>{formatCurrency(period.expenses?.assets?.[key])}</b>
                                          </Typography>
                                        </TableCell>
                                      );
                                    })}
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(calculateRowTotal('expenses.assets', key))}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))}

                              {/* Depreciation Line */}
                              <TableRow>
                                <TableCell align="left" sx={{ pl: 12 }}>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>Depreciation</b>
                                  </Typography>
                                </TableCell>
                                {columns.map((col, colIndex) => {
                                  const period = data.periods[colIndex] || {};
                                  return (
                                    <TableCell key={colIndex} align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(period.expenses?.assets?.depreciation)}</b>
                                      </Typography>
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(calculateRowTotal('expenses.assets', 'depreciation'))}</b>
                                  </Typography>
                                </TableCell>
                              </TableRow>

                              {/* Simplified Depreciation Details */}
                              {(() => {
                                const allAssets = {};
                                data.periods.forEach(period => {
                                  const details = period.expenses?.assets?.depreciation_details || [];
                                  details.forEach(asset => {
                                    if (!allAssets[asset.asset_name]) {
                                      allAssets[asset.asset_name] = {
                                        name: asset.asset_name,
                                        amounts: Array(columns.length).fill(0)
                                      };
                                    }
                                  });
                                });

                                data.periods.forEach((period, periodIndex) => {
                                  const details = period.expenses?.assets?.depreciation_details || [];
                                  details.forEach(asset => {
                                    if (allAssets[asset.asset_name] && periodIndex < columns.length) {
                                      allAssets[asset.asset_name].amounts[periodIndex] = asset.depreciated_amount;
                                    }
                                  });
                                });

                                return Object.values(allAssets).map((asset, index) => (
                                  <TableRow key={index}>
                                    <TableCell align="left" sx={{ pl: 15 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{asset.name}</b>
                                      </Typography>
                                    </TableCell>
                                    {asset.amounts.map((amount, colIndex) => (
                                      <TableCell key={colIndex} align="right">
                                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                          <b>{formatCurrency(amount)}</b>
                                        </Typography>
                                      </TableCell>
                                    ))}
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(asset.amounts.reduce((sum, a) => sum + a, 0))}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ));
                              })()}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>

                    
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Net Surplus/Deficit */}
          <TableRow sx={{ backgroundColor: "#f6ffed" }}>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Net Surplus/Deficit</b>
              </Typography>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography 
                    fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                    color={period.net_surplus_deficit >= 0 ? "success.main" : "error.main"}
                  >
                    <b>{formatCurrency(period.net_surplus_deficit)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography 
                fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                color={data.summary?.net_surplus_deficit >= 0 ? "success.main" : "error.main"}
              >
                <b>{formatCurrency(data.summary?.net_surplus_deficit)}</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};


const LoanPortfolioTable = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    pipeline: false,
    performance: false,
    financials: false,
    disbursedDetails: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!data || !data.periods || data.periods.length === 0) {
    return <Typography>Loading data or no data available...</Typography>;
  }

  // Determine columns based on period type
  const getColumns = () => {
    const periodMap = {
      yearly: 'Year',
      quarterly: 'Quarter',
      monthly: 'Month',
      weekly: 'Week'
    };

    return data.periods.map(period => ({
      name: period.name,
      start: period.start_date,
      end: period.end_date,
      type: periodMap[data.periodic_state] || 'Period'
    }));
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate row totals
  const calculateRowTotal = (category, key) => {
    if (!data.periods || data.periods.length === 0) return 0;
    return data.periods.reduce((sum, period) => sum + (period.metrics?.[category]?.[key] || 0), 0);
  };

  // Get first period for category keys
  const firstPeriod = data.periods[0] || {};

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 500,
        minWidth: 800,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="loan portfolio table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Loan Portfolio Metrics</b>
              </Typography>
            </TableCell>
            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
                <Typography variant="caption" display="block">
                  {formatDate(col.start)} - {formatDate(col.end)}
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Loan Pipeline Section */}
          <TableRow sx={{ backgroundColor: "#e6f7ff" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('pipeline')}>
                  {expandedCategories.pipeline ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Loan Pipeline</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(
                      period.metrics?.loans_requested?.amount +
                      period.metrics?.loans_recommended?.amount +
                      period.metrics?.loans_approved?.amount +
                      period.metrics?.loans_disbursed?.amount
                    )}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(
                  data.summary?.total_requested +
                  data.summary?.total_recommended +
                  data.summary?.total_approved +
                  data.summary?.total_disbursed
                )}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Loan Pipeline Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.pipeline} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Loans Requested */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Loans Requested</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.loans_requested?.amount)}</b>
                              <Typography variant="caption" display="block">
                                ({period.metrics?.loans_requested?.count})
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_requested)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Loans Recommended */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Loans Recommended</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.loans_recommended?.amount)}</b>
                              <Typography variant="caption" display="block">
                                ({period.metrics?.loans_recommended?.count})
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_recommended)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Loans Approved */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Loans Approved</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.loans_approved?.amount)}</b>
                              <Typography variant="caption" display="block">
                                <b>({period.metrics?.loans_approved?.count})</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_approved)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Loans Disbursed */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('disbursedDetails')}>
                            {expandedCategories.disbursedDetails ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Loans Disbursed</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.loans_disbursed?.amount)}</b>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>({period.metrics?.loans_disbursed?.count})</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_disbursed)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Disbursed Loans Details */}
                    <TableRow>
                      <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                        <Collapse in={expandedCategories.disbursedDetails} timeout="auto" unmountOnExit>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ pl: 12 }}><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Member</b></Typography></TableCell>
                                <TableCell align="right"><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Amount</b></Typography></TableCell>
                                <TableCell align="right"><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Disbursement Date</b></Typography></TableCell>
                                <TableCell align="right"><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Repayment Start</b></Typography></TableCell>
                                <TableCell align="right"><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Repayment End</b></Typography></TableCell>
                                <TableCell align="right"><Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>Interest Rate</b></Typography></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {data.periods.flatMap((period, periodIndex) => 
                                period.metrics?.loans_disbursed?.details?.map((loan, loanIndex) => (
                                  <TableRow key={`${periodIndex}-${loanIndex}`}>
                                    <TableCell sx={{ pl: 12 }}>
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{loan.member_name}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(loan.amount)}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatDate(loan.disbursement_date)}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatDate(loan.repayment_starts)}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatDate(loan.repayment_ends)}</b>
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{loan.interest_rate ? `${loan.interest_rate}%` : 'N/A'}</b>
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Loan Performance Section */}
          <TableRow sx={{ backgroundColor: "#f6ffed" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('performance')}>
                  {expandedCategories.performance ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Loan Performance</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(
                      period.metrics?.loans_repaid?.amount +
                      period.metrics?.completed_loans?.amount
                    )}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(
                  data.summary?.total_repaid +
                  data.summary?.total_completed
                )}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Loan Performance Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.performance} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Loans Repaid */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Loans Repaid</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.loans_repaid?.amount)}</b>
                              {/* <Typography variant="caption" display="block">
                                ({period.metrics?.loans_repaid?.count})
                              </Typography> */}
                              <Typography variant="caption" display="block">
                                <b>Principal: {formatCurrency(period.metrics?.loans_repaid?.principal)}</b>
                              </Typography>
                              <Typography variant="caption" display="block">
                                <b>Interest Received: {formatCurrency(period.metrics?.loans_repaid?.interest)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_repaid)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* completed Loans */}
                    <TableRow sx={{ backgroundColor: "#fff1f0" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Completed Loans (Fully paid off)</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.completed_loans?.amount)}</b>
                              <Typography variant="caption" display="block">
                                <b>({period.metrics?.completed_loans?.count})</b>
                              </Typography>
                              <Typography variant="caption" display="block">
                                <b>Principal: {formatCurrency(period.metrics?.completed_loans?.principal)}</b>
                              </Typography>
                              <Typography variant="caption" display="block">
                                <b>Interest: {formatCurrency(period.metrics?.completed_loans?.interest)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_completed)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Outstanding Loans */}
                    <TableRow sx={{ backgroundColor: "#fff7e6" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Outstanding Loans</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.outstanding_loans?.amount)}</b>
                              <Typography variant="caption" display="block">
                                <b>({period.metrics?.outstanding_loans?.count})</b>
                              </Typography>
                              <Typography variant="caption" display="block">
                                <b>Principal: {formatCurrency(period.metrics?.outstanding_loans?.principal)}</b>
                              </Typography>
                              <Typography variant="caption" display="block">
                                <b>Accrued Interest: {formatCurrency(period.metrics?.outstanding_loans?.interest)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_outstanding)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Financial Metrics Section */}
          <TableRow sx={{ backgroundColor: "#f0f0ff" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('financials')}>
                  {expandedCategories.financials ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Financial Metrics</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(
                      period.metrics?.interest_earned +
                      period.metrics?.revenue_from_fees?.total
                    )}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(
                  data.summary?.total_interest_earned +
                  data.summary?.total_fees_earned
                )}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Financial Metrics Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.financials} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Interest Earned */}
                    <TableRow sx={{ backgroundColor: "#e6e6ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Interest Earned</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.interest_earned)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_interest_earned)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Revenue from Fees */}
                    <TableRow sx={{ backgroundColor: "#e6e6ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Revenue from Fees</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.revenue_from_fees?.total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_fees_earned)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Fee Breakdown */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 12 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Application Fees</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.revenue_from_fees?.application_fees)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(
                            data.periods.reduce((sum, period) => 
                              sum + (period.metrics?.revenue_from_fees?.application_fees || 0), 0)
                          )}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell align="left" sx={{ pl: 12 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Processing Fees</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.revenue_from_fees?.processing_fees)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(
                            data.periods.reduce((sum, period) => 
                              sum + (period.metrics?.revenue_from_fees?.processing_fees || 0), 0)
                          )}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};


const AssetsTable = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    fixedAssets: false,
    currentAssets: false,
    cash: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!data || !data.periods) return null;

  // Determine columns based on period type
  const getColumns = () => {
    switch (data.periodic_state) {
      case 'yearly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'quarterly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'monthly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'weekly':
        return data.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      default:
        return [];
    }
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate row totals
  const calculateRowTotal = (category, key) => {
    if (!data.periods || data.periods.length === 0) return 0;
    
    if (category === 'total_fixed_assets') {
      return data.total_fixed_assets;
    }
    if (category === 'total_current_assets') {
      return data.total_current_assets;
    }
    if (category === 'total_assets') {
      return data.total_assets;
    }
    if (category === 'fixed_assets') {
      return data.periods.reduce((sum, period) => sum + (period.fixed_assets[key] || 0), 0);
    }
    if (category === 'current_assets') {
      return data.periods.reduce((sum, period) => sum + (period.current_assets[key] || 0), 0);
    }
    return 0;
  };

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 500,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="assets table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Asset Categories</b>
              </Typography>
            </TableCell>
            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
                <Typography variant="caption" display="block">
                  {formatDate(col.start)} - {formatDate(col.end)}
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Fixed Assets */}
          <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('fixedAssets')}>
                  {expandedCategories.fixedAssets ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Fixed Assets</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex];
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.fixed_assets?.total_fixed_assets)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('total_fixed_assets'))}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Fixed Assets Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.fixedAssets} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Illiquid Assets */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Illiquid Assets</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex];
                        const total = period.fixed_assets?.illiquid_assets?.reduce((sum, asset) => sum + asset.asset_value, 0) || 0;
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(calculateRowTotal('fixed_assets', 'total_fixed_assets'))}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Illiquid Assets Details - Show only for period acquired */}
                    {columns.map((col, colIndex) => {
                      const period = data.periods[colIndex];
                      const assets = period.fixed_assets?.illiquid_assets || [];
                      if (assets.length === 0) return null;
                      
                      return assets.map((asset, idx) => (
                        <TableRow key={`asset-${colIndex}-${idx}`}>
                          <TableCell align="left" sx={{ pl: 9 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{asset.asset_name} (Acquired: {formatDate(asset.date_acquired)})</b>
                            </Typography>
                          </TableCell>
                          {columns.map((_, i) => (
                            <TableCell key={i} align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{i === colIndex ? formatCurrency(asset.asset_value) : '—'}</b>
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(asset.asset_value)}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    })}

                    {/* Logistic Items */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Logistic Items</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex];
                        const total = period.fixed_assets?.logistic_items?.reduce((sum, item) => sum + item.total_value, 0) || 0;
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(total)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(calculateRowTotal('fixed_assets', 'total_logistic_value'))}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Logistic Items Details - Show only for period acquired */}
                    {columns.map((col, colIndex) => {
                      const period = data.periods[colIndex];
                      const items = period.fixed_assets?.logistic_items || [];
                      if (items.length === 0) return null;
                      
                      return items.map((item, idx) => (
                        <TableRow key={`logistic-${colIndex}-${idx}`}>
                          <TableCell align="left" sx={{ pl: 9 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{item.item_name} ({item.quantity} units)</b>
                            </Typography>
                          </TableCell>
                          {columns.map((_, i) => (
                            <TableCell key={i} align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{i === colIndex ? formatCurrency(item.total_value) : '—'}</b>
                              </Typography>
                            </TableCell>
                          ))}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(item.total_value)}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    })}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Current Assets */}
          <TableRow sx={{ backgroundColor: "#fff0f3" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('currentAssets')}>
                  {expandedCategories.currentAssets ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Current Assets</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex];
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.current_assets?.total_current_assets)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('total_current_assets'))}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Current Assets Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.currentAssets} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Cash and Equivalents */}
                    <TableRow sx={{ backgroundColor: "#fff8f9" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Box display="flex" alignItems="center">
                          <IconButton size="small" onClick={() => toggleCategory('cash')}>
                            {expandedCategories.cash ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                          </IconButton>
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>Cash</b>
                          </Typography>
                        </Box>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex];
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.current_assets?.cash_and_equivalents?.total_cash)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(
                            data.periods.reduce((total, period) => {
                              return total + (period.current_assets?.cash_and_equivalents?.total_cash || 0);
                            }, 0)
                          )}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Cash Details */}
                    <Collapse in={expandedCategories.cash} timeout="auto" unmountOnExit>
                      <Box sx={{ ml: 3 }}>
                        {/* Main Wallet */}
                        <TableRow>
                          <TableCell align="left" sx={{ pl: 9 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>Main Wallet</b>
                            </Typography>
                          </TableCell>
                          {columns.map((col, colIndex) => {
                            const period = data.periods[colIndex];
                            return (
                              <TableCell key={colIndex} align="right">
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>{formatCurrency(period.current_assets?.cash_and_equivalents?.main_wallet)}</b>
                                </Typography>
                              </TableCell>
                            );
                          })}
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(
                                data.periods.reduce((total, period) => {
                                  return total + (period.current_assets?.cash_and_equivalents?.main_wallet || 0);
                                }, 0)
                              )}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>

                        {/* Bank Accounts */}
                        {data.periods[0]?.current_assets?.cash_and_equivalents?.bank_accounts?.map((account, idx) => (
                          <TableRow key={`bank-${idx}`}>
                            <TableCell align="left" sx={{ pl: 9 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{account.bank_name} - {account.account_name}</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = data.periods[colIndex];
                              const bankAccount = period.current_assets?.cash_and_equivalents?.bank_accounts?.find(
                                a => a.account_number === account.account_number
                              );
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(bankAccount?.balance || 0)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(account.balance)}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </Box>
                    </Collapse>

                    {/* Loans Outstanding */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Loans Outstanding</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex];
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.current_assets?.loans_outstanding)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(
                            data.periods.reduce((total, period) => {
                              return total + (period.current_assets?.loans_outstanding || 0);
                            }, 0)
                          )}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Accounts Receivable */}
                    {/* <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Accounts Receivable</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex];
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.current_assets?.accounts_receivable)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(
                            data.periods.reduce((total, period) => {
                              return total + (period.current_assets?.accounts_receivable || 0);
                            }, 0)
                          )}</b>
                        </Typography>
                      </TableCell>
                    </TableRow> */}

                    {/* Short-Term Investments */}
                    {/* <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Short-Term Investments</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex];
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.current_assets?.short_term_investments)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(
                            data.periods.reduce((total, period) => {
                              return total + (period.current_assets?.short_term_investments || 0);
                            }, 0)
                          )}</b>
                        </Typography>
                      </TableCell>
                    </TableRow> */}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Total Assets */}
          <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total Assets</b>
              </Typography>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex];
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.total_assets)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('total_assets'))}</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};


const LiabilitiesTable = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    memberSavings: false,
    shares: false,
    externalFunding: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!data || !data.periods || data.periods.length === 0) {
    return <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
      <b>Loading data or no data available...</b>
    </Typography>;
  }

  // Determine columns based on period type
  const getColumns = () => {
    const periodMap = {
      yearly: 'Year',
      quarterly: 'Quarter',
      monthly: 'Month',
      weekly: 'Week'
    };

    return data.periods.map(period => ({
      name: period.name,
      start: period.start_date,
      end: period.end_date,
      type: periodMap[data.periodic_state] || 'Period'
    }));
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate row totals
  const calculateRowTotal = (category, subCategory = null) => {
    if (!data.periods || data.periods.length === 0) return 0;
    return data.periods.reduce((sum, period) => {
      if (subCategory) {
        return sum + (period.liabilities[category]?.[subCategory] || 0);
      }
      if (category === 'external_funding') {
        return sum + (period.liabilities[category]?.total || 0);
      }
      return sum + (period.liabilities[category] || 0);
    }, 0);
  };

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 600,
        overflowY: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="liabilities table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Liability Categories</b>
              </Typography>
            </TableCell>
            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
                <Typography variant="caption" display="block">
                  {formatDate(col.start)} - {formatDate(col.end)}
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Member Savings */}
          <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('memberSavings')}>
                  {expandedCategories.memberSavings ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Member Savings</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(
                      (period.liabilities?.member_savings?.boafo_savings || 0) + 
                      (period.liabilities?.member_savings?.extra_savings || 0) + 
                      (period.liabilities?.member_savings?.minimum_balance || 0) 
                    )}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(
                  calculateRowTotal('member_savings', 'boafo_savings') + 
                  calculateRowTotal('member_savings', 'extra_savings') +
                  calculateRowTotal('member_savings', 'minimum_balance')
                )}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Member Savings Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.memberSavings} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Boafo Savings */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Boafo Savings</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.liabilities?.member_savings?.boafo_savings || 0)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(calculateRowTotal('member_savings', 'boafo_savings'))}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Extra Savings */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Extra Savings</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.liabilities?.member_savings?.extra_savings || 0)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(calculateRowTotal('member_savings', 'extra_savings'))}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>
                    
                    {/* Minimum Balance */}
                    <TableRow>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Minimum Balance</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.liabilities?.member_savings?.minimum_balance || 0)}</b>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(calculateRowTotal('member_savings', 'minimum_balance'))}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Member Shares */}
          <TableRow sx={{ backgroundColor: "#fff0f3" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('shares')}>
                  {expandedCategories.shares ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Member Shares</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.liabilities?.member_shares || 0)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('member_shares'))}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* External Funding */}
          <TableRow sx={{ backgroundColor: "#ffedd8" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('externalFunding')}>
                  {expandedCategories.externalFunding ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>External Funding</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.liabilities?.external_funding?.total || 0)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(calculateRowTotal('external_funding'))}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* External Funding Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.externalFunding} timeout="auto" unmountOnExit>
                <Box sx={{ margin: 1 }}>
                  {columns.map((col, colIndex) => {
                    const period = data.periods[colIndex] || {};
                    const investments = period.liabilities?.external_funding?.investments || [];
                    
                    if (investments.length === 0) return null;

                    return (
                      <Box key={colIndex} mb={3}>
                        <Typography fontWeight="bold" mb={1} fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{col.name} ({formatDate(col.start)} to {formatDate(col.end)})</b>
                        </Typography>
                        <Table size="small" aria-label="investment details">
                          <TableHead>
                            <TableRow>
                              <TableCell><b>Investor</b></TableCell>
                              <TableCell><b>Company</b></TableCell>
                              <TableCell><b>Description</b></TableCell>
                              <TableCell align="right"><b>Amount payable</b></TableCell>
                              {/* <TableCell align="right"><b>Outstanding</b></TableCell> */}
                              <TableCell><b>Interest</b></TableCell>
                              <TableCell><b>Date</b></TableCell>
                              <TableCell><b>Status</b></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {investments.map((investment, idx) => (
                              <TableRow key={`${colIndex}-${idx}`}>
                                <TableCell>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{investment.investor_name}</b>
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{investment.company}</b>
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{investment.description}</b>
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(investment.amount_investing)}</b>
                                  </Typography>
                                </TableCell>
                                {/* <TableCell align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(investment.amount_left)}</b>
                                  </Typography>
                                </TableCell> */}
                                <TableCell>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{investment.interest_rate}% ({investment.interest_type})</b>
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{investment.date}</b>
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{investment.status}</b>
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    );
                  })}
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Total Liabilities */}
          <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total Liabilities</b>
              </Typography>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(period.liabilities?.total_liabilities || 0)}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(data.total_liabilities || 0)}</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const BalanceSheetReport = ({ assetsData, liabilitiesData }) => {
  const [expandedSections, setExpandedSections] = useState({
    assets: false,
    liabilities: false,
    fixedAssets: false,
    currentAssets: false,
    cash: false,
    memberSavings: false,
    externalFunding: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!assetsData || !liabilitiesData || !assetsData.periods || !liabilitiesData.periods) {
    return (
      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
        <b>Loading data or no data available...</b>
      </Typography>
    );
  }

  // Determine columns based on period type (use assets data as reference)
  const getColumns = () => {
    switch (assetsData.periodic_state) {
      case 'yearly':
        return assetsData.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'quarterly':
        return assetsData.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'monthly':
        return assetsData.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      case 'weekly':
        return assetsData.periods.map(period => ({
          name: period.name,
          start: period.start_date,
          end: period.end_date
        }));
      default:
        return [];
    }
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate row totals for assets
  const calculateAssetsRowTotal = (category, key) => {
    if (!assetsData.periods || assetsData.periods.length === 0) return 0;
    
    if (category === 'total_fixed_assets') {
      return assetsData.total_fixed_assets;
    }
    if (category === 'total_current_assets') {
      return assetsData.total_current_assets;
    }
    if (category === 'total_assets') {
      return assetsData.total_assets;
    }
    if (category === 'fixed_assets') {
      return assetsData.periods.reduce((sum, period) => sum + (period.fixed_assets[key] || 0), 0);
    }
    if (category === 'current_assets') {
      return assetsData.periods.reduce((sum, period) => sum + (period.current_assets[key] || 0), 0);
    }
    return 0;
  };

  // Calculate row totals for liabilities
  const calculateLiabilitiesRowTotal = (category, subCategory = null) => {
    if (!liabilitiesData.periods || liabilitiesData.periods.length === 0) return 0;
    return liabilitiesData.periods.reduce((sum, period) => {
      if (subCategory) {
        return sum + (period.liabilities[category]?.[subCategory] || 0);
      }
      if (category === 'external_funding') {
        return sum + (period.liabilities[category]?.total || 0);
      }
      return sum + (period.liabilities[category] || 0);
    }, 0);
  };

  return (
    <Box>
      <Box
        sx={{
          overflowX: "auto",
          maxHeight: "80vh",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "7px",
            height: "7px",
          },
          "&::-webkit-scrollbar-track": {
            background: "#f1f1f1",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#555",
          },
        }}
      >
        {/* Assets Section */}
        <Box mb={4}>
          {/* <Box 
            display="flex" 
            alignItems="center" 
            onClick={() => toggleSection('assets')} 
            sx={{ cursor: 'pointer', mb: 1 }}
          >
            <IconButton size="small">
              {expandedSections.assets ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            </IconButton>
            <Typography variant="h6" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" sx={{ fontWeight: 'bold' }}>
              <b>Assets</b>
            </Typography>
          </Box>
          
          <Collapse in={expandedSections.assets} timeout="auto" unmountOnExit> */}
            <Table size="small" aria-label="assets section">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Asset Categories</b>
                    </Typography>
                  </TableCell>
                  {columns.map((col, index) => (
                    <TableCell key={index} align="right">
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{col.name}</b>
                      </Typography>
                      <Typography variant="caption" display="block">
                        {formatDate(col.start)} - {formatDate(col.end)}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Total</b>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Fixed Assets */}
                <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                  <TableCell align="left">
                    <Box display="flex" alignItems="center">
                      <IconButton size="small" onClick={() => toggleSection('fixedAssets')}>
                        {expandedSections.fixedAssets ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                      </IconButton>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>Fixed Assets</b>
                      </Typography>
                    </Box>
                  </TableCell>
                  {columns.map((col, colIndex) => {
                    const period = assetsData.periods[colIndex];
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(period.fixed_assets?.total_fixed_assets)}</b>
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(calculateAssetsRowTotal('total_fixed_assets'))}</b>
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Fixed Assets Details */}
                <TableRow>
                  <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                    <Collapse in={expandedSections.fixedAssets} timeout="auto" unmountOnExit>
                      <Table size="small">
                        <TableBody>
                          {/* Illiquid Assets */}
                          <TableRow>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Illiquid Assets</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = assetsData.periods[colIndex];
                              const total = period.fixed_assets?.illiquid_assets?.reduce((sum, asset) => sum + asset.asset_value, 0) || 0;
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(total)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(calculateAssetsRowTotal('fixed_assets', 'total_fixed_assets'))}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Logistic Items */}
                          <TableRow>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Logistic Items</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = assetsData.periods[colIndex];
                              const total = period.fixed_assets?.logistic_items?.reduce((sum, item) => sum + item.total_value, 0) || 0;
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(total)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(calculateAssetsRowTotal('fixed_assets', 'total_logistic_value'))}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>

                {/* Current Assets */}
                <TableRow sx={{ backgroundColor: "#fff0f3" }}>
                  <TableCell align="left">
                    <Box display="flex" alignItems="center">
                      <IconButton size="small" onClick={() => toggleSection('currentAssets')}>
                        {expandedSections.currentAssets ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                      </IconButton>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>Current Assets</b>
                      </Typography>
                    </Box>
                  </TableCell>
                  {columns.map((col, colIndex) => {
                    const period = assetsData.periods[colIndex];
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(period.current_assets?.total_current_assets)}</b>
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(calculateAssetsRowTotal('total_current_assets'))}</b>
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Current Assets Details */}
                <TableRow>
                  <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                    <Collapse in={expandedSections.currentAssets} timeout="auto" unmountOnExit>
                      <Table size="small">
                        <TableBody>
                          {/* Cash and Equivalents */}
                          <TableRow sx={{ backgroundColor: "#fff8f9" }}>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Box display="flex" alignItems="center">
                                <IconButton size="small" onClick={() => toggleSection('cash')}>
                                  {expandedSections.cash ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                                </IconButton>
                                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                  <b>Cash</b>
                                </Typography>
                              </Box>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = assetsData.periods[colIndex];
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(period.current_assets?.cash_and_equivalents?.total_cash)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(
                                  assetsData.periods.reduce((total, period) => {
                                    return total + (period.current_assets?.cash_and_equivalents?.total_cash || 0);
                                  }, 0)
                                )}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Cash Details */}
                          <Collapse in={expandedSections.cash} timeout="auto" unmountOnExit>
                            <Box sx={{ ml: 3 }}>
                              {/* Main Wallet */}
                              <TableRow>
                                <TableCell align="left" sx={{ pl: 9 }}>
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>Main Wallet</b>
                                  </Typography>
                                </TableCell>
                                {columns.map((col, colIndex) => {
                                  const period = assetsData.periods[colIndex];
                                  return (
                                    <TableCell key={colIndex} align="right">
                                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                        <b>{formatCurrency(period.current_assets?.cash_and_equivalents?.main_wallet)}</b>
                                      </Typography>
                                    </TableCell>
                                  );
                                })}
                                <TableCell align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(
                                      assetsData.periods.reduce((total, period) => {
                                        return total + (period.current_assets?.cash_and_equivalents?.main_wallet || 0);
                                      }, 0)
                                    )}</b>
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            </Box>
                          </Collapse>

                          {/* Loans Outstanding */}
                          <TableRow>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Loans Outstanding</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = assetsData.periods[colIndex];
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(period.current_assets?.loans_outstanding)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(
                                  assetsData.periods.reduce((total, period) => {
                                    return total + (period.current_assets?.loans_outstanding || 0);
                                  }, 0)
                                )}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Accounts Receivable */}
                          {/* <TableRow>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Accounts Receivable</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = assetsData.periods[colIndex];
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(period.current_assets?.accounts_receivable)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(
                                  assetsData.periods.reduce((total, period) => {
                                    return total + (period.current_assets?.accounts_receivable || 0);
                                  }, 0)
                                )}</b>
                              </Typography>
                            </TableCell>
                          </TableRow> */}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>

                {/* Total Assets */}
                <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                  <TableCell align="left">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Total Assets</b>
                    </Typography>
                  </TableCell>
                  {columns.map((col, colIndex) => {
                    const period = assetsData.periods[colIndex];
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(period.total_assets)}</b>
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(calculateAssetsRowTotal('total_assets'))}</b>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          {/* </Collapse> */}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Liabilities Section */}
        <Box>
          {/* <Box 
            display="flex" 
            alignItems="center" 
            onClick={() => toggleSection('liabilities')} 
            sx={{ cursor: 'pointer', mb: 1 }}
          >
            <IconButton size="small">
              {expandedSections.liabilities ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            </IconButton>
            <Typography variant="h6" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" sx={{ fontWeight: 'bold' }}>
              <b>Liabilities</b>
            </Typography>
          </Box>
          
          <Collapse in={expandedSections.liabilities} timeout="auto" unmountOnExit> */}
            <Table size="small" aria-label="liabilities section">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Liability Categories</b>
                    </Typography>
                  </TableCell>
                  {columns.map((col, index) => (
                    <TableCell key={index} align="right">
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{col.name}</b>
                      </Typography>
                      <Typography variant="caption" display="block">
                        {formatDate(col.start)} - {formatDate(col.end)}
                      </Typography>
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Total</b>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Member Savings */}
                <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                  <TableCell align="left">
                    <Box display="flex" alignItems="center">
                      <IconButton size="small" onClick={() => toggleSection('memberSavings')}>
                        {expandedSections.memberSavings ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                      </IconButton>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>Member Savings</b>
                      </Typography>
                    </Box>
                  </TableCell>
                  {columns.map((col, colIndex) => {
                    const period = liabilitiesData.periods[colIndex] || {};
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(
                            (period.liabilities?.member_savings?.boafo_savings || 0) + 
                            (period.liabilities?.member_savings?.extra_savings || 0) +
                            (period.liabilities?.member_savings?.minimum_balance || 0)
                          )}</b>
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(
                        calculateLiabilitiesRowTotal('member_savings', 'boafo_savings') + 
                        calculateLiabilitiesRowTotal('member_savings', 'extra_savings') +
                        calculateLiabilitiesRowTotal('member_savings', 'minimum_balance')
                      )}</b>
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Member Savings Details */}
                <TableRow>
                  <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
                    <Collapse in={expandedSections.memberSavings} timeout="auto" unmountOnExit>
                      <Table size="small">
                        <TableBody>
                          {/* Boafo Savings */}
                          <TableRow>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Boafo Savings</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = liabilitiesData.periods[colIndex] || {};
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(period.liabilities?.member_savings?.boafo_savings || 0)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(calculateLiabilitiesRowTotal('member_savings', 'boafo_savings'))}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>

                          {/* Extra Savings */}
                          <TableRow>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Extra Savings</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = liabilitiesData.periods[colIndex] || {};
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(period.liabilities?.member_savings?.extra_savings || 0)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(calculateLiabilitiesRowTotal('member_savings', 'extra_savings'))}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>
                          
                          {/* Minimum Balance */}
                          <TableRow>
                            <TableCell align="left" sx={{ pl: 6 }}>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>Minimum Balance</b>
                              </Typography>
                            </TableCell>
                            {columns.map((col, colIndex) => {
                              const period = liabilitiesData.periods[colIndex] || {};
                              return (
                                <TableCell key={colIndex} align="right">
                                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                    <b>{formatCurrency(period.liabilities?.member_savings?.minimum_balance || 0)}</b>
                                  </Typography>
                                </TableCell>
                              );
                            })}
                            <TableCell align="right">
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                                <b>{formatCurrency(calculateLiabilitiesRowTotal('member_savings', 'minimum_balance'))}</b>
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>

                {/* Member Shares */}
                <TableRow sx={{ backgroundColor: "#fff0f3" }}>
                  <TableCell align="left">
                    <Box display="flex" alignItems="center">
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>Member Shares</b>
                      </Typography>
                    </Box>
                  </TableCell>
                  {columns.map((col, colIndex) => {
                    const period = liabilitiesData.periods[colIndex] || {};
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(period.liabilities?.member_shares || 0)}</b>
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(calculateLiabilitiesRowTotal('member_shares'))}</b>
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* External Funding */}
                <TableRow sx={{ backgroundColor: "#ffedd8" }}>
                  <TableCell align="left">
                    <Box display="flex" alignItems="center">
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>External Funding</b>
                      </Typography>
                    </Box>
                  </TableCell>
                  {columns.map((col, colIndex) => {
                    const period = liabilitiesData.periods[colIndex] || {};
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(period.liabilities?.external_funding?.total || 0)}</b>
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(calculateLiabilitiesRowTotal('external_funding'))}</b>
                    </Typography>
                  </TableCell>
                </TableRow>

                {/* Total Liabilities */}
                <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                  <TableCell align="left">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Total Liabilities</b>
                    </Typography>
                  </TableCell>
                  {columns.map((col, colIndex) => {
                    const period = liabilitiesData.periods[colIndex] || {};
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(period.liabilities?.total_liabilities || 0)}</b>
                        </Typography>
                      </TableCell>
                    );
                  })}
                  <TableCell align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{formatCurrency(liabilitiesData.total_liabilities || 0)}</b>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          {/* </Collapse> */}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Net Worth Section */}
        <Box>
          <Typography variant="h6" fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" sx={{ fontWeight: 'bold', mb: 2 }}>
            <b>Net Worth</b>
          </Typography>
          
          <Table size="small" aria-label="net worth section">
            <TableHead>
              <TableRow>
                <TableCell align="left">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Category</b>
                  </Typography>
                </TableCell>
                {columns.map((col, index) => (
                  <TableCell key={index} align="right">
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>{col.name}</b>
                    </Typography>
                    <Typography variant="caption" display="block">
                      {formatDate(col.start)} - {formatDate(col.end)}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Total</b>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell align="left">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Total Assets</b>
                  </Typography>
                </TableCell>
                {columns.map((col, colIndex) => {
                  const period = assetsData.periods[colIndex];
                  return (
                    <TableCell key={colIndex} align="right">
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{formatCurrency(period?.total_assets || 0)}</b>
                      </Typography>
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(calculateAssetsRowTotal('total_assets'))}</b>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell align="left">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Total Liabilities</b>
                  </Typography>
                </TableCell>
                {columns.map((col, colIndex) => {
                  const period = liabilitiesData.periods[colIndex] || {};
                  return (
                    <TableCell key={colIndex} align="right">
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{formatCurrency(period.liabilities?.total_liabilities || 0)}</b>
                      </Typography>
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(liabilitiesData.total_liabilities || 0)}</b>
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                <TableCell align="left">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Net Worth</b>
                  </Typography>
                </TableCell>
                {columns.map((col, colIndex) => {
                  const assetsPeriod = assetsData.periods[colIndex] || {};
                  const liabilitiesPeriod = liabilitiesData.periods[colIndex] || {};
                  const netWorth = (assetsPeriod.total_assets || 0) - (liabilitiesPeriod.liabilities?.total_liabilities || 0);
                  return (
                    <TableCell key={colIndex} align="right">
                      <Typography sx={{color: netWorth <= 0 ?'red':'black'}} fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{formatCurrency(netWorth)}</b>
                      </Typography>
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  <Typography sx={{color: (calculateAssetsRowTotal('total_assets') - (liabilitiesData.total_liabilities || 0)) <= 0 ?'red':'black'}} fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(
                      calculateAssetsRowTotal('total_assets') - (liabilitiesData.total_liabilities || 0)
                    )}</b>
                  </Typography>
                </TableCell>
              </TableRow>
              
              {/* Performance Analysis Row */}
              <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                <TableCell align="left">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>Performance</b>
                  </Typography>
                </TableCell>
                {columns.map((col, colIndex) => {
                  if (colIndex === 0) {
                    return (
                      <TableCell key={colIndex} align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Base Period</b>
                        </Typography>
                      </TableCell>
                    );
                  }
                  
                  const prevAssets = assetsData.periods[colIndex - 1]?.total_assets || 0;
                  const prevLiabilities = liabilitiesData.periods[colIndex - 1]?.liabilities?.total_liabilities || 0;
                  const prevNetWorth = prevAssets - prevLiabilities;
                  
                  
                  const currentAssets = assetsData.periods[colIndex]?.total_assets || 0;
                  const currentLiabilities = liabilitiesData.periods[colIndex]?.liabilities?.total_liabilities || 0;
                  const currentNetWorth = currentAssets - currentLiabilities;
                  
                  const difference = (prevNetWorth - currentNetWorth);
                  const percentageChange = prevNetWorth !== 0 ? (difference / Math.abs(prevNetWorth)) * 100 : 0;
                  
                  return (
                    <TableCell key={colIndex} align="right">
                      <Box>
                        <Typography 
                          fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                          sx={{ color: difference >= 0 ? 'green' : 'red' }}
                        >
                          <b>{formatCurrency(difference)}</b>
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ color: currentNetWorth >= prevNetWorth ? 'green' : 'red' }}
                        >
                          <b>({Math.abs(percentageChange).toFixed(2)}% {currentNetWorth >= prevNetWorth ? '↑' : '↓'})</b>
                        </Typography>
                      </Box>
                    </TableCell>
                  );
                })}
                <TableCell align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>-</b>
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
};

const LoanScheduleReportTable = ({ data }) => {
  const [expandedCategories, setExpandedCategories] = useState({
    payments: false,
    loanStatus: false,
    delinquentLoans: false
  });

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  if (!data || !data.periods || data.periods.length === 0) {
    return (
      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
        <b>Loading data or no data available...</b>
      </Typography>
    );
  }

  // Determine columns based on period type
  const getColumns = () => {
    const periodMap = {
      yearly: 'Year',
      quarterly: 'Quarter',
      monthly: 'Month',
      weekly: 'Week'
    };

    return data.periods.map(period => ({
      name: period.name,
      start: period.start_date,
      end: period.end_date,
      type: periodMap[data.periodic_state] || 'Period'
    }));
  };

  const columns = getColumns();

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GH', {
      style: 'currency',
      currency: 'GHS',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box
      sx={{
        overflowX: "auto",
        maxHeight: 500,
        minWidth: 800,
        overflow: "auto",
        "&::-webkit-scrollbar": {
          width: "7px",
          height: "7px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#888",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#555",
        },
      }}
    >
      <Table size="small" aria-label="loan schedule report table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Loan Schedule Metrics</b>
              </Typography>
            </TableCell>
            {columns.map((col, index) => (
              <TableCell key={index} align="right">
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>{col.name}</b>
                </Typography>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                  <b>{formatDate(col.start)} - {formatDate(col.end)}</b>
                </Typography>
              </TableCell>
            ))}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>Total</b>
              </Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Payments Summary Section */}
          <TableRow sx={{ backgroundColor: "#e6f7ff" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('payments')}>
                  {expandedCategories.payments ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Payments Summary</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{formatCurrency(
                      (period.metrics?.due_payments?.total || 0) +
                      (period.metrics?.paid_payments?.total || 0) +
                      (period.metrics?.overdue_payments?.total || 0)
                    )}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{formatCurrency(
                  data.summary?.total_repayment_due +
                  data.summary?.total_repayment_paid +
                  data.summary?.total_repayment_overdue
                )}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Payments Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.payments} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Due Payments */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Due Payments</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.due_payments?.total)}</b>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>({period.metrics?.due_payments?.count} payments)</b>
                              </Typography>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Principal: {formatCurrency(period.metrics?.due_payments?.principal)}</b>
                              </Typography>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Interest: {formatCurrency(period.metrics?.due_payments?.interest)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_repayment_due)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Paid Payments */}
                    <TableRow sx={{ backgroundColor: "#f6ffed" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Paid Payments</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.paid_payments?.total)}</b>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>({period.metrics?.paid_payments?.count} payments)</b>
                              </Typography>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Principal: {formatCurrency(period.metrics?.paid_payments?.principal)}</b>
                              </Typography>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Interest: {formatCurrency(period.metrics?.paid_payments?.interest)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_repayment_paid)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Overdue Payments */}
                    <TableRow sx={{ backgroundColor: "#fff1f0" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Overdue Payments</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(period.metrics?.overdue_payments?.total)}</b>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>({period.metrics?.overdue_payments?.count} payments)</b>
                              </Typography>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Principal: {formatCurrency(period.metrics?.overdue_payments?.principal)}</b>
                              </Typography>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Interest: {formatCurrency(period.metrics?.overdue_payments?.interest)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{formatCurrency(data.summary?.total_repayment_overdue)}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Loan Status Section */}
          <TableRow sx={{ backgroundColor: "#fff7e6" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('loanStatus')}>
                  {expandedCategories.loanStatus ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Loan Status Summary</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{period.metrics?.active_loans?.count + period.metrics?.completed_loans?.count || 0}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{data.summary?.active_loans + data.summary?.completed_loans || 0}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Loan Status Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.loanStatus} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableBody>
                    {/* Active Loans */}
                    <TableRow sx={{ backgroundColor: "#f0f7ff" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Active Loans</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{period.metrics?.active_loans?.count}</b>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Outstanding: {formatCurrency(period.metrics?.active_loans?.total_outstanding)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{data.summary?.active_loans}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>

                    {/* Completed Loans */}
                    <TableRow sx={{ backgroundColor: "#f6ffed" }}>
                      <TableCell align="left" sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Completed Loans</b>
                        </Typography>
                      </TableCell>
                      {columns.map((col, colIndex) => {
                        const period = data.periods[colIndex] || {};
                        return (
                          <TableCell key={colIndex} align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{period.metrics?.completed_loans?.count}</b>
                              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif" variant="caption" display="block">
                                <b>Repaid: {formatCurrency(period.metrics?.completed_loans?.total_repaid)}</b>
                              </Typography>
                            </Typography>
                          </TableCell>
                        );
                      })}
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>{data.summary?.completed_loans}</b>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>

          {/* Delinquent Loans Section */}
          <TableRow sx={{ backgroundColor: "#fff1f0" }}>
            <TableCell align="left">
              <Box display="flex" alignItems="center">
                <IconButton size="small" onClick={() => toggleCategory('delinquentLoans')}>
                  {expandedCategories.delinquentLoans ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
                </IconButton>
                <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                  <b>Delinquent Loans</b>
                </Typography>
              </Box>
            </TableCell>
            {columns.map((col, colIndex) => {
              const period = data.periods[colIndex] || {};
              return (
                <TableCell key={colIndex} align="right">
                  <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                    <b>{period.metrics?.overdue_payments?.delinquent_loans?.length || 0}</b>
                  </Typography>
                </TableCell>
              );
            })}
            <TableCell align="right">
              <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                <b>{data.summary?.delinquent_loans || 0}</b>
              </Typography>
            </TableCell>
          </TableRow>

          {/* Delinquent Loans Details */}
          <TableRow>
            <TableCell colSpan={columns.length + 2} style={{ padding: 0 }}>
              <Collapse in={expandedCategories.delinquentLoans} timeout="auto" unmountOnExit>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ pl: 6 }}>
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Member</b>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Loan ID</b>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Total Overdue</b>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Oldest Overdue (days)</b>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                          <b>Overdue Payments</b>
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.periods.flatMap((period, periodIndex) => 
                      period.metrics?.overdue_payments?.delinquent_loans?.map((loan, loanIndex) => (
                        <TableRow key={`${periodIndex}-${loanIndex}`}>
                          <TableCell sx={{ pl: 6 }}>
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{loan.member_name}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{loan.loan_id}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{formatCurrency(loan.total_overdue)}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{loan.oldest_overdue_days}</b>
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                              <b>{loan.overdue_payments?.length || 0}</b>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Collapse>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};





function FinancialStatements() {
  const [value, setValue] = useState("1");
  const [incomeStatement, setIncomeStatement] = useState([]);
  const [inflowStatement, setInflowStatement] = useState([])
  const [expenseStatement, setExpenseStatement] = useState([])
  const [incomeAndExpenseStatement, setIncomeAndExpenseStatement] = useState([])
  const [assetStatement, setAssetStatement] = useState([])
  const [loanReport, setLoanReport] = useState([])
  const [loanType, setLoanType] = useState(false)
  const [loanSchedule, setLoanSchedule] = useState([])
  const [liabilityStatement, setLiabilityStatement] = useState([])
  const [start_date, setStart_date] = useState("");
  const [end_date, setEnd_date] = useState("");
  const [period, setPeriod] = useState("Quarterly"); // Yearly or Quarterly
  const [selected_year, setSelected_year] = useState(2025);
  const [selected_month, setSelected_month] = useState("");
  const location = useLocation();
  const society_name = location.state && location.state.society_name;
  const society_id = location.state && location.state.society_id;
  const [clusters, setClusters] = useState([]);
  const [selected_cluster, setSelected_cluster] = useState({});
  const [communities, setCommunities] = useState([]);
  const [selected_com, setSelected_com] = useState({});
  const [loan_accounts, setLoan_accounts] = useState([]);
  const [selected_la, setSelected_la] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selected_group, setSelected_group] = useState({});
  const navigate = useNavigate()

  const viewLoanDashboard = () => {
    navigate("/loans/account", { state: { id: selected_la, accounts: loan_accounts, society_id: society_id, society_name: society_name } });
  }

  const monthOptions = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const currencyFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "GHS",
  });

  const fetch_groups = () => {
    api
      .get(`/association/get/groups/in/communities/${selected_com?.id}`, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      })
      .then(function (response) {
        setGroups(response.data?.groups);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    if (selected_com?.id) {
      fetch_groups();
    }
  }, [selected_com]);

  const fetch_communities = () => {
    api
      .get(`/association/get/communities/in/cluster/${selected_cluster?.id}`, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      })
      .then(function (response) {
        setCommunities(response.data?.communities);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    if (selected_cluster?.id) {
      fetch_communities();
    }
  }, [selected_cluster]);

  const fetch_clusters = () => {
    api
      .get(`/association/get/clusters/${society_id}`, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      })
      .then(function (response) {
        setClusters(response.data?.all);
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  useEffect(() => {
    fetch_clusters();
  }, [society_id]);

  const fetchIncomeStatement = async () => {
    const data = new FormData();
    data.append("society_id", society_id);
    data.append("periodic_state", period);
    data.append("start_date", start_date);
    data.append("end_date", end_date);
    data.append("cluster_id", selected_cluster.id ? selected_cluster.id : 0);
    data.append("community_id", selected_com.id ? selected_com.id : 0);
    data.append("group_id", selected_group.id ? selected_group.id : 0);
    data.append("selected_year", selected_year);
    data.append("selected_month", selected_month);
    if(selected_la){
      data.append("loan_account_id", selected_la);
      data.append("account_id", selected_la);
    }
    

    toast.promise(
      api.get("/account/loan/hh", {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setLoan_accounts(response.data);

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/get/incomes", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setIncomeStatement(response.data);

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/get/liabilities", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setLiabilityStatement(response.data);

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/get/assets", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setAssetStatement(response.data);

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/get/inflows", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setInflowStatement(response.data);

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/get/expenses", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setExpenseStatement(response.data);

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/get/income/and/expenses", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setIncomeAndExpenseStatement(response.data);
          // console.log(response.data)

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/get/loan/portfolio", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setLoanReport(response.data);
          // console.log(response.data)

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
    
    toast.promise(
      api.post("/dashboards/loan/schedule/report", data, {
        headers: {
          Authorization: "Bearer " + fetchToken(),
        },
      }),
      {
        loading: "Loading Info...",
        success: (response) => {
          setLoanSchedule(response.data);
          // console.log(response.data)

          return "Successfully Loaded";
        },
        error: (error) => {
          console.log(error);
          return "An error Occurred, try again later";
        },
      }
    );
  };
  
  useEffect(() => {
    fetchIncomeStatement();
  }, [
    society_id,
    period,
    selected_year,
    selected_month,
    start_date,
    end_date,
    selected_cluster.id,
    selected_com.id,
    selected_group.id,
    selected_la
  ]);

  useEffect(() => {
    fetchIncomeStatement();
  }, []);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <Grid container xs={12} md={12} sx={{ mx: 5 }}>
      <Grid item xs={12} md={12} sx={{ mr: 4 }}>
        <Box sx={{ width: "100%", typography: "body1" }}>
          <Typography
            variant="h4"
            sx={{ mb: 3 }}
            fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
          >
            <b>Financial Statements</b>
          </Typography>

          <Typography
            variant="h4"
            sx={{ mb: 3, textAlign:'center' }}
            fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
          >
            <b>{society_name}</b>
          </Typography>

          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="financial statements tabs"
              >
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Inflow Statement</b>
                    </Typography>
                  }
                  value="1"
                />
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Income Statement</b>
                    </Typography>
                  }
                  value="2"
                />
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Expense Statement</b>
                    </Typography>
                  }
                  value="3"
                />
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Income and Expense Statement</b>
                    </Typography>
                  }
                  value="4"
                />
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Assets</b>
                    </Typography>
                  }
                  value="5"
                />
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Liabilities</b>
                    </Typography>
                  }
                  value="6"
                />
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Balance Sheet</b>
                    </Typography>
                  }
                  value="7"
                />
                <Tab
                  label={
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                      <b>Loan Reports</b>
                    </Typography>
                  }
                  value="8"
                />
              </Tabs>
            </Box>

            <TabPanel value="1">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box>
              <Divider variant="middle" /> */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  {/* <b>Income Statement</b> */}
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>

              <InflowsTable data={inflowStatement} />
            </TabPanel>

            <TabPanel value="2">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box>
              <Divider variant="middle" /> */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  {/* <b>Income Statement</b> */}
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>


              <IncomeStatementTable data={incomeStatement} />
            </TabPanel>

            <TabPanel value="3">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box>
              <Divider variant="middle" /> */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  <b></b>
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>

              <ExpensesTable data={expenseStatement} />
            </TabPanel>

            <TabPanel value="4">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box>
              <Divider variant="middle" /> */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  {/* <b>Income Statement</b> */}
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>

              <IncomeExpenseTable data={incomeAndExpenseStatement} />

            </TabPanel>
            
            <TabPanel value="5">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box>
              <Divider variant="middle" /> */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  {/* <b>Income Statement</b> */}
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>

              <AssetsTable data={assetStatement} />

            </TabPanel>

            <TabPanel value="6">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box>
              <Divider variant="middle" /> */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  {/* <b>Income Statement</b> */}
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>
              <LiabilitiesTable data={liabilityStatement} />
            </TabPanel>

            <TabPanel value="7">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box>
              <Divider variant="middle" /> */}
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  {/* <b>Income Statement</b> */}
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>
                  <BalanceSheetReport assetsData={assetStatement} liabilitiesData={liabilityStatement} />
            </TabPanel>
            
            <TabPanel value="8">
              {/* <Box sx={{ display: "flex", justifyContent: "center" }}>
                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="From"
                  name="start_date"
                  onChange={(e) => setStart_date(e.target.value)}
                  value={start_date}
                  sx={{ mr: 1 }}
                />

                <TextField
                  type="date"
                  size="small"
                  required
                  variant="outlined"
                  helperText="To"
                  name="end_date"
                  onChange={(e) => setEnd_date(e.target.value)}
                  value={end_date}
                  sx={{ mr: 1 }}
                />
              </Box> */}
              <Divider variant="middle" sx={{my:5}}>
                <Button
                  variant="contained"
                  onClick={viewLoanDashboard}
                  sx={{
                    mr: 1,
                    borderRadius: 2,
                    boxShadow:
                      "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                    "&:hover": {
                      boxShadow:
                        "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                      backgroundColor:"#14213d" ,
                    },
                  }}
                >
                  View Dashboard
                </Button>
              </Divider>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
              >
                <Typography
                  variant="h5"
                  sx={{ mb: 2 }}
                  fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"
                >
                  {/* <b>Income Statement</b> */}
                </Typography>
                <Box>
                  <Button
                    variant={period === "Yearly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Yearly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Yearly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Annual
                  </Button>
                  <Button
                    variant={period === "Quarterly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Quarterly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Quarterly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Quarterly
                  </Button>
                  <Button
                    variant={period === "Monthly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Monthly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Monthly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Monthly
                  </Button>
                  <Button
                    variant={period === "Weekly" ? "contained" : "outlined"}
                    onClick={() => setPeriod("Weekly")}
                    sx={{
                      mr: 1,
                      borderRadius: 2,
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor:
                          period === "Weekly" ? "#14213d" : "inherit",
                      },
                    }}
                  >
                    Weekly
                  </Button>
                </Box>
                <Box>
                  <TextField
                    sx={{ m: 1 }}
                    size="small"
                    name="selected_year"
                    type="number"
                    value={selected_year}
                    onChange={(e) => setSelected_year(e.target.value)}
                    inputProps={{
                      min: 1900,
                      max: new Date().getFullYear() + 2,
                      step: 1,
                    }}
                    label="Year"
                  />

                  <Autocomplete
                    sx={{ m: 1, width: 180 }}
                    size="small"
                    options={monthOptions}
                    value={selected_month}
                    onChange={(event, newValue) => setSelected_month(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Month"
                        name="selected_month"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={clusters}
                  getOptionLabel={(option) => option.cluster_name}
                  value={selected_cluster?selected_cluster.cluster:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_cluster({
                        id: newValue.cluster_id,
                        cluster: newValue.cluster_name,
                      });
                    } else {
                      setSelected_cluster(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.cluster_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.cluster_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Cluster"
                      name="selected_cluster"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 200 }}
                  size="small"
                  options={communities}
                  getOptionLabel={(option) => option.community_name}
                  value={selected_com?selected_com.community:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_com({
                        id: newValue.community_id,
                        community: newValue.community_name,
                      });
                    } else {
                      setSelected_com(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.community_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.community_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Community"
                      name="selected_community"
                    />
                  )}
                />

                <Autocomplete
                  sx={{ m: 1, width: 300 }}
                  size="small"
                  options={groups}
                  getOptionLabel={(option) => option.association_name}
                  value={selected_group?selected_group.group:null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setSelected_group({
                        id: newValue.association_id,
                        group: newValue.association_name,
                      });
                    } else {
                      setSelected_group(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{option.association_name}</b>
                      </Typography>
                    </li>
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.association_id === value?.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Group"
                      name="selected_group"
                    />
                  )}
                />
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Clusters</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_cluster({})
                    setSelected_com({})
                    setSelected_group({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All Communities</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_com({})
                  }} 
                />
                <Chip 
                  size='small'
                  label={<Typography variant='subtitle' fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif"><b>All groups</b></Typography>}
                  sx={{
                    borderRadius:2,
                    m:1,
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor:'#da1e37',
                    color:'#fff',
                    '&:hover': {
                      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)',
                      backgroundColor:'#14213d',
                    },
                  }} 
                  onClick={() => {
                    setSelected_group({})
                  }} 
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "center", overflowX: 'auto', '&::-webkit-scrollbar': {
                    height: '5px',
                  },
                  '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                  },
                  '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                }}}>
                  <Chip
                    label={
                      <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>All Loan Products</b>
                      </Typography>
                    }
                    size="small"
                    variant={!selected_la ? "filled" : "outlined"}
                    sx={{
                      m: 1,
                      cursor:'pointer',
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      backgroundColor:
                      !selected_la ? "#fff" : "#344e41",
                      color: !selected_la ? "#000" : "#fff",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor: "#14213d",
                        color: "#000",
                      },
                    }}
                    onClick={() => setSelected_la(null)}
                  />
                  {
                    loan_accounts && loan_accounts?.map((account) => (
                      <Chip
                        label={
                          <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                            <b>{account.account_name}</b>
                          </Typography>
                        }
                        size="small"
                        variant={selected_la && selected_la === account.id ? "filled" : "outlined"}
                        sx={{
                          m: 1,
                          cursor:'pointer',
                          boxShadow:
                            "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                          backgroundColor:
                          selected_la && selected_la === account.id ? "#fff" : "#344e41",
                          color: selected_la && selected_la === account.id ? "#000" : "#fff",
                          "&:hover": {
                            boxShadow:
                              "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                            backgroundColor: "#14213d",
                            color: "#000",
                          },
                        }}
                        onClick={() => setSelected_la(account.id)}
                      />
                    ))
                  }
                </Box>
                <Box sx={{ display: "flex", justifyContent: "center", overflowX: 'auto', '&::-webkit-scrollbar': {
                    height: '5px',
                  },
                  '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                  },
                  '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '10px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                }}}>
                  <Button
                    onClick={() => setLoanType(prev => !prev)}
                    variant="contained"
                    size="medium"
                    sx={{
                      borderRadius: 2,
                      m: 1,
                      backgroundColor: "#000",
                      color: "#fff",
                      boxShadow:
                        "0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.1)",
                      "&:hover": {
                        boxShadow:
                          "0 8px 12px rgba(0, 0, 0, 0.2), 0 8px 30px rgba(0, 0, 0, 0.2)",
                        backgroundColor: "#14213d",
                      },
                    }}
                  >
                    <Typography fontFamily="'Nunito', 'Outfit', 'Roboto', 'Source Sans 3', sans-serif">
                        <b>{loanType?'Portfolio Report': 'Schedule Report'}</b>
                    </Typography>
                  </Button>
                </Box>

                  {
                    loanType?
                    <LoanScheduleReportTable data={loanSchedule} />
                    :
                    <LoanPortfolioTable data={loanReport} />
                  }
                  

            </TabPanel>

          </TabContext>
        </Box>
      </Grid>
    </Grid>
  );
}

export default FinancialStatements;
